import { formatDate } from "@/lib/utils";
import React from "react";
import PropTypes from "prop-types";
import Modal from "@/components/ui/modal";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../api/apiClient";
import { API_ENDPOINTS } from "../../../api/header";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ScanBarcode,
  Package,
  Store,
  Calendar,
  History,
  Info,
  ExternalLink,
  ArrowRight,
  TrendingUp,
  Clock,
  User,
  Tag,
  Shield,
  Activity,
} from "lucide-react";

import { Skeleton } from "@/components/ui/skeleton";

const IS_SUPERMARKET = import.meta.env.VITE_APP_PROFILE === "supermarket";

const SerialDetailModal = ({ open, onOpenChange, serialId, serialNumber }) => {
  const { data: rawSerial, isLoading } = useQuery({
    queryKey: ["serial-track", serialNumber || serialId],
    queryFn: async () => {
      const endpoint = serialNumber
        ? API_ENDPOINTS.INVENTORY.SERIAL_TRACK(serialNumber)
        : API_ENDPOINTS.INVENTORY.SERIAL_DETAILS(serialId);

      const res = await apiClient.get(endpoint);
      return res.data.data;
    },
    enabled: !!(serialId || serialNumber) && open,
  });

  // Normalise: track endpoint nests product/store inside inventory;
  // list-based opens pass them at the root. Prefer root, fall back to inventory.
  const serial = rawSerial
    ? {
      ...rawSerial,
      product: rawSerial.product ?? rawSerial.inventory?.product ?? null,
      store: rawSerial.store ?? rawSerial.inventory?.store ?? null,
    }
    : null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "success";
      case "sold":
        return "secondary";
      case "damaged":
        return "destructive";
      case "lost":
        return "destructive";
      case "expired":
        return "destructive";
      case "returned":
        return "warning";
      case "under_inspection":
        return "warning";
      case "refurbished":
        return "outline";
      default:
        return "secondary";
    }
  };

  const formatCondition = (condition) => {
    if (!condition) return "Brand New";
    const lower = String(condition).toLowerCase();
    if (
      lower === "used" ||
      lower === "pre-owned" ||
      lower === "preowned" ||
      lower === "refurbished"
    ) {
      return "Pre-Owned/Refurbished";
    }
    if (lower === "new") return "Brand New";
    return condition.charAt(0).toUpperCase() + condition.slice(1);
  };

  const infoItem = (icon, label, value) => (
    <div className="flex items-start gap-3 py-2">
      <div className="mt-0.5 p-1 rounded-full bg-primary/10 text-primary">
        {icon}
      </div>
      <div>
        <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
          {label}
        </p>
        <div className="text-sm font-semibold">{value || "-"}</div>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Serial Number Details"
      icon={ScanBarcode}
      maxWidth="max-w-2xl"
    >
      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
          <Separator />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : serial ? (
        <div className="space-y-6">
          {/* Header Card */}
          <div className="flex flex-col md:flex-row md:items-center justify-between p-5 rounded-2xl bg-muted/30 border gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <ScanBarcode className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-black font-mono tracking-tighter">
                  {serial.serial_number}
                </h3>
              </div>
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Package size={14} />
                {serial.product?.name}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge
                variant={
                  serial.status_info?.variant || getStatusColor(serial.status)
                }
                className="px-3"
              >
                {serial.status_info?.label || serial.status}
              </Badge>
              <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-widest">
                Current Status
              </span>
            </div>
          </div>

          {/* Quick Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {infoItem(<Store size={14} />, "Location", serial.store?.name)}
            {infoItem(
              <Tag size={14} />,
              "Product SKU",
              serial.product?.sku || "-",
            )}
            {infoItem(
              <Shield size={14} />,
              "Warranty",
              serial.product?.warranty_timeframe || "No Warranty",
            )}
            {infoItem(
              <Activity size={14} />,
              "Condition",
              formatCondition(serial.product?.condition),
            )}
            {infoItem(
              <Clock size={14} />,
              "Last Activity",
              serial.updated_at
                ? formatDate(new Date(serial.updated_at))
                : "-",
            )}
            {IS_SUPERMARKET &&
              infoItem(
                <Calendar size={14} />,
                "Expiry Date",
                serial.expiry_date
                  ? formatDate(new Date(serial.expiry_date)) +
                  (serial.is_expired
                    ? " (Expired)"
                    : ` (${serial.days_until_expiry} days left)`)
                  : "-",
              )}
            {infoItem(
              <TrendingUp size={14} />,
              "Total Movements",
              `${serial.transactions?.length || 0} stages`,
            )}
          </div>

          {serial.inventory?.notes && (
            <div className="p-4 rounded-xl bg-primary/5 dark:bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Info className="h-4 w-4 text-primary" />
                <span className="text-[10px] font-extrabold uppercase tracking-widest text-primary">
                  Inventory Mapping Notes
                </span>
              </div>
              <p className="text-xs text-foreground font-medium leading-relaxed italic">
                {serial.inventory.notes}
              </p>
            </div>
          )}

          <Separator />

          {/* Lifecycle Timeline */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 font-bold text-sm uppercase tracking-wider text-muted-foreground">
              <History size={16} />
              <span>Movement Lifecycle</span>
            </div>

            <ScrollArea className="h-[300px] pr-4">
              <div className="relative pl-8 space-y-8 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-muted">
                {serial.transactions && serial.transactions.length > 0 ? (
                  serial.transactions.map((tx, idx) => (
                    <div key={tx.id} className="relative">
                      {/* Timeline Dot */}
                      <div
                        className={`absolute -left-[29px] top-1 w-4 h-4 rounded-full border-2 border-background z-10 ${idx === 0
                          ? "bg-primary animate-pulse"
                          : "bg-muted-foreground/30"
                          }`}
                      />

                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold capitalize">
                            {(tx.type || tx.meta?.reference_type || "Update").replace(/_/g, " ")}
                          </span>
                          <span className="text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                            {formatDate(new Date(tx.created_at))}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground leading-relaxed italic">
                          {tx.notes || tx.meta?.notes ||
                            `Stock ${(tx.type || tx.meta?.reference_type || "").includes("in") || (tx.type || tx.meta?.reference_type) === "receipt" ? "increased" : "decreased"} at ${tx.store?.name || "the store"}.`}
                        </p>
                        <div className="flex items-center justify-between pt-1">
                          <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-medium">
                            <User size={10} />
                            {tx.creator?.name || "System"}
                          </div>
                          {(tx.reference_id || tx.meta?.reference_id) && (
                            <div className="flex items-center gap-1 text-[10px] text-primary font-bold cursor-pointer hover:underline">
                              Ref: {String(tx.reference_id || tx.meta?.reference_id).slice(0, 8)}
                              <ExternalLink size={10} />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-10 text-center space-y-2 opacity-50">
                    <Info size={32} />
                    <p className="text-sm">
                      No transaction history recorded yet.
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>
      ) : (
        <div className="py-20 text-center text-muted-foreground">
          <Info className="mx-auto h-12 w-12 mb-4 opacity-20" />
          <p>We couldn't load the details for this serial number.</p>
        </div>
      )}
    </Modal>
  );
};

SerialDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  serialId: PropTypes.string,
  serialNumber: PropTypes.string,
};

export default SerialDetailModal;
